"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Form,
  Select,
  Button,
  message,
} from "antd";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import { useTranslation } from "react-i18next";

import { createRelative } from "../../../Profile/redux/thunks/relativesThunks";
import { fetchUsers } from "../../../Profile/redux/thunks/userThunks";

import { selectEffectiveUserId } from "../../redux/selectors/sousProfileSelectors";

const { Option } = Select;

/* ============================================================
   HELPERS
============================================================ */

const looksLikeEmail = (value = "") =>
  /@/.test(String(value));

const normalizeRelation = (value = "") =>
  String(value)
    .trim()
    .toLowerCase();

/* ============================================================
   COMPONENT
============================================================ */

export default function AddRelativeForm({
  slotMember,
  onDone,
  onCancel,
}) {
  const { t } = useTranslation("sousProfile");

  const dispatch = useDispatch();

  const [form] = Form.useForm();

  const [
    messageApi,
    contextHolder,
  ] = message.useMessage();

  const [
    searchValue,
    setSearchValue,
  ] = useState("");

  /* ============================================================
     REDUX
  ============================================================ */

  const userId = useSelector(
    selectEffectiveUserId
  );

  const users = useSelector((state) =>
    Object.values(
      state.user?.usersById || {}
    )
  );

  /* ============================================================
     RELATION OPTIONS
  ============================================================ */

  const allRelationshipOptions =
    useMemo(
      () => [
        {
          value: "mother",
          label: t(
            "family.relationship.mother",
            {
              defaultValue: "Mother",
            }
          ),
        },

        {
          value: "father",
          label: t(
            "family.relationship.father",
            {
              defaultValue: "Father",
            }
          ),
        },

        {
          value: "brother",
          label: t(
            "family.relationship.brother",
            {
              defaultValue: "Brother",
            }
          ),
        },

        {
          value: "sister",
          label: t(
            "family.relationship.sister",
            {
              defaultValue: "Sister",
            }
          ),
        },

        {
          value: "husband",
          label: t(
            "family.relationship.husband",
            {
              defaultValue: "Husband",
            }
          ),
        },

        {
          value: "wife",
          label: t(
            "family.relationship.wife",
            {
              defaultValue: "Wife",
            }
          ),
        },

        {
          value: "son",
          label: t(
            "family.relationship.son",
            {
              defaultValue: "Son",
            }
          ),
        },

        {
          value: "daughter",
          label: t(
            "family.relationship.daughter",
            {
              defaultValue: "Daughter",
            }
          ),
        },

        /*
         * Gardé uniquement comme fallback
         * si AddRelativeForm est utilisé ailleurs
         * sans contexte FamilyTree.
         */
        {
          value: "spouse",
          label: t(
            "family.relationship.spouse",
            {
              defaultValue: "Spouse",
            }
          ),
        },
      ],
      [t]
    );

  /* ============================================================
     FILTER RELATIONS BY FAMILY TREE SLOT
  ============================================================ */

  const relationshipOptions =
    useMemo(() => {
      /*
       * Si AddRelativeForm est utilisé depuis
       * un autre endroit que FamilyTree et qu'il
       * n'y a pas allowedRelations :
       *
       * → garder la liste complète.
       */
      if (
        !Array.isArray(
          slotMember?.allowedRelations
        )
      ) {
        return allRelationshipOptions;
      }

      const allowedRelations =
        slotMember.allowedRelations.map(
          normalizeRelation
        );

      /*
       * IMPORTANT :
       * Si le FamilyTree fournit un filtre,
       * on ne montre QUE ces relations.
       */
      return allRelationshipOptions.filter(
        (option) =>
          allowedRelations.includes(
            normalizeRelation(
              option.value
            )
          )
      );
    }, [
      slotMember?.allowedRelations,
      allRelationshipOptions,
    ]);

  /* ============================================================
     AUTO SELECT WHEN THERE IS ONLY ONE RELATION
  ============================================================ */

  useEffect(() => {
    const currentRelationship =
      form.getFieldValue(
        "relationshipType"
      );

    const allowedValues =
      relationshipOptions.map(
        (option) => option.value
      );

    /*
     * Mother card
     * → automatically Mother
     *
     * Father card
     * → automatically Father
     *
     * Wife/Husband
     * → automatically selected
     */
    if (
      relationshipOptions.length === 1
    ) {
      form.setFieldsValue({
        relationshipType:
          relationshipOptions[0].value,
      });

      return;
    }

    /*
     * Si le slot change et que l'ancienne
     * relation n'est plus valide,
     * on la supprime.
     */
    if (
      currentRelationship &&
      !allowedValues.includes(
        currentRelationship
      )
    ) {
      form.setFieldsValue({
        relationshipType: undefined,
      });
    }
  }, [
    form,
    slotMember?.id,
    relationshipOptions,
  ]);

  /* ============================================================
     LOAD USERS
  ============================================================ */

  useEffect(() => {
    if (!users.length) {
      dispatch(fetchUsers());
    }
  }, [
    dispatch,
    users.length,
  ]);

  /* ============================================================
     USER SEARCH
  ============================================================ */

  const suggestions = useMemo(() => {
    if (
      !searchValue ||
      looksLikeEmail(searchValue)
    ) {
      return [];
    }

    const query =
      searchValue.toLowerCase();

    return users
      .filter((user) => {
        const firstName =
          user?.firstName || "";

        const lastName =
          user?.lastName || "";

        const fullName =
          `${firstName} ${lastName}`
            .trim()
            .toLowerCase();

        return fullName.includes(query);
      })
      .slice(0, 5);
  }, [
    users,
    searchValue,
  ]);

  /* ============================================================
     SUBMIT
  ============================================================ */

  const handleSubmit = useCallback(
    async (values) => {
      if (!userId) {
        messageApi.error(
          t(
            "family.errors.notAuthenticated"
          )
        );

        return;
      }

      try {
        /*
         * mode="tags"
         * peut retourner un tableau.
         */
        const rawValue =
          Array.isArray(
            values.relativeQuery
          )
            ? values.relativeQuery[0]
            : values.relativeQuery;

        const query =
          rawValue?.trim();

        if (!query) {
          throw new Error(
            t(
              "family.errors.provideEmailOrName"
            )
          );
        }

        /* ======================================================
           SECURITY / FRONT VALIDATION

           Vérifie que la relation sélectionnée
           appartient bien aux relations autorisées
           par le slot du FamilyTree.
        ====================================================== */

        const allowedValues =
          relationshipOptions.map(
            (option) => option.value
          );

        if (
          allowedValues.length > 0 &&
          !allowedValues.includes(
            values.relationshipType
          )
        ) {
          throw new Error(
            "Invalid relationship for this family member."
          );
        }

        /* ======================================================
           PAYLOAD EXISTANT
        ====================================================== */

        const payload = {
          userId,

          relationshipType:
            values.relationshipType,
        };

        if (looksLikeEmail(query)) {
          payload.relativeEmail =
            query;
        } else {
          payload.relativeSearch =
            query;
        }

        /* ======================================================
           CREATE RELATIVE
        ====================================================== */

        const action =
          await dispatch(
            createRelative(payload)
          );

        /* ======================================================
           SUCCESS
        ====================================================== */

        if (
          createRelative.fulfilled.match(
            action
          )
        ) {
          const created =
            action.payload || null;

          /* ====================================================
             STATUS
          ==================================================== */

          let isAccepted = false;

          if (created) {
            const status =
              (
                created.status ||
                created.state ||
                created.relationshipStatus ||
                ""
              )
                .toString()
                .toLowerCase();

            const acceptedFlags = [
              created.accepted,
              created.isAccepted,
              created.confirmed,
            ];

            isAccepted =
              status === "accepted" ||
              status === "active" ||
              acceptedFlags.some(
                Boolean
              );
          }

          /* ====================================================
             SUCCESS MESSAGE
          ==================================================== */

          if (
            created &&
            isAccepted
          ) {
            messageApi.success(
              t(
                "family.success.added"
              )
            );
          } else {
            messageApi.success(
              t(
                "family.success.inviteSent"
              )
            );
          }

          /* ====================================================
             RELATIVE ID
          ==================================================== */

          const relativeId =
            created?.relativeId ||
            created?.relativeUser?.id ||
            created?.linkedUserId ||
            null;

          /* ====================================================
             RETURN TO FAMILY TREE
          ==================================================== */

          onDone?.({
            relativeId,

            relativeEmail:
              payload.relativeEmail ||
              null,

            relativeSearch:
              payload.relativeSearch ||
              null,

            relationshipType:
              payload.relationshipType,

            linkedUser:
              isAccepted
                ? created?.relativeUser ||
                  null
                : null,
          });

          /* ====================================================
             RESET FORM
          ==================================================== */

          form.resetFields();

          setSearchValue("");

          return;
        }

        /* ======================================================
           REDUX ERROR
        ====================================================== */

        throw new Error(
          action.payload ||
            t(
              "family.errors.failedAdd"
            )
        );
      } catch (error) {
        const errorMessage =
          error?.message ||
          t(
            "family.errors.addError"
          );

        messageApi.error(
          errorMessage
        );
      }
    },
    [
      dispatch,
      form,
      messageApi,
      userId,
      t,
      onDone,
      relationshipOptions,
    ]
  );

  /* ============================================================
     RENDER
  ============================================================ */

  return (
    <>
      {contextHolder}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
      >
        {/* ====================================================
            USER
        ==================================================== */}

        <Form.Item
          name="relativeQuery"
          label={t(
            "family.fields.query.label"
          )}
          rules={[
            {
              required: true,

              message: t(
                "family.fields.query.required"
              ),
            },
          ]}
        >
          <Select
            mode="tags"
            showSearch
            placeholder={t(
              "family.fields.query.placeholder"
            )}
            onSearch={
              setSearchValue
            }
            filterOption={false}
            notFoundContent={t(
              "family.fields.query.notFound"
            )}
          >
            {suggestions.map(
              (user) => {
                const userKey =
                  user.id ||
                  user._id ||
                  user.email;

                const fullName =
                  `${user.firstName || ""} ${
                    user.lastName || ""
                  }`.trim();

                return (
                  <Option
                    key={userKey}
                    value={fullName}
                  >
                    {fullName}
                  </Option>
                );
              }
            )}
          </Select>
        </Form.Item>

        {/* ====================================================
            RELATIONSHIP
        ==================================================== */}

        <Form.Item
          name="relationshipType"
          label={t(
            "family.fields.relationship.label"
          )}
          rules={[
            {
              required: true,

              message: t(
                "family.fields.relationship.required"
              ),
            },
          ]}
        >
          <Select
            placeholder={t(
              "family.fields.relationship.placeholder"
            )}
          >
            {relationshipOptions.map(
              (relationship) => (
                <Option
                  key={
                    relationship.value
                  }
                  value={
                    relationship.value
                  }
                >
                  {relationship.label}
                </Option>
              )
            )}
          </Select>
        </Form.Item>

        {/* ====================================================
            ACTIONS
        ==================================================== */}

        <div
          style={{
            display: "flex",

            justifyContent:
              "flex-end",

            gap: 8,

            marginTop: 8,
          }}
        >
          <Button
            onClick={onCancel}
          >
            {t("common.cancel") ||
              "Annuler"}
          </Button>

          <Button
            type="primary"
            onClick={() =>
              form.submit()
            }
            style={{
              background:
                "#006eea",

              borderColor:
                "#006eea",

              borderRadius:
                8,

              fontWeight:
                600,
            }}
          >
            {t(
              "family.actions.save"
            ) || "Ajouter"}
          </Button>
        </div>
      </Form>
    </>
  );
}